/**
 * parseTenantSubdomain — extract the canonical tenant slug from a host string.
 *
 *   aeeiee-staging.3mote.com -> "aeeiee"
 *   aeeiee-prod.3mote.com    -> "aeeiee"
 *   aeeiee.3mote.com         -> "aeeiee"   (post-cutover)
 *   localhost / 127.0.0.1    -> "aeeiee"   (hardcoded dev fallback)
 *
 * The slug is sent as `X-Tenant-Subdomain` to the API. The `-staging` /
 * `-prod` stage suffixes are an architectural artifact of the parallel-run
 * window and are stripped here so the BE only ever sees the canonical
 * tenant identifier.
 *
 * Throws on hosts that don't end in `.3mote.com` (and aren't localhost) to
 * surface configuration mistakes loudly rather than silently sending an
 * empty header.
 */

const APEX = '.3mote.com'

/**
 * The reserved first label of the central auth origin —
 * `login.3mote.com` (prod) / `login-staging.3mote.com` (staging). It is
 * the ONE origin registered with Google + Apple (neither provider
 * accepts wildcard origins), served by the same SPA bundle via a
 * dedicated CloudFront alias. Because session cookies write to
 * `.3mote.com`, a sign-in completed there is valid on every tenant
 * subdomain. No tenant may ever claim this slug — the infra runbook reserves
 * it (tenant onboarding is manual today), and the future runtime create-tenant
 * path must reject it alongside the `-staging` / `-prod` suffix rule.
 */
export const CENTRAL_AUTH_LABEL = 'login'

/** Strip an optional `:port` and return the bare hostname. */
function hostnameOf(host: string): string {
  return host.split(':')[0] ?? ''
}

/**
 * True for localhost-family dev hosts (`localhost:5173`, `127.0.0.1`).
 * Shared by the tenant parser's dev fallback and the login page's
 * inline-vs-central SSO branch (`.3mote.com` cookies can't reach
 * localhost, so the central-origin hop would strand the session there).
 */
export function isLocalhostHost(host: string): boolean {
  return host.startsWith('localhost') || host.startsWith('127.0.0.1')
}

/**
 * True when `host` IS the central auth origin: `login.3mote.com` or
 * `login-staging.3mote.com` (port-tolerant). Never true for localhost —
 * local dev keeps the inline SSO flow, so nothing may route it into the
 * central-host experience. Deliberately exact-match (no `login-prod`):
 * prod hosts are suffix-less by convention.
 */
export function isCentralAuthHost(host: string): boolean {
  const hostname = hostnameOf(host)
  return (
    hostname === `${CENTRAL_AUTH_LABEL}${APEX}` ||
    hostname === `${CENTRAL_AUTH_LABEL}-staging${APEX}`
  )
}

export function parseTenantSubdomain(host: string): string {
  if (isLocalhostHost(host)) {
    return 'aeeiee'
  }

  // Strip ":port"
  const hostname = hostnameOf(host)

  // Strip apex .3mote.com
  if (!hostname.endsWith(APEX)) {
    throw new Error(`Unexpected host: ${host}`)
  }

  // The central auth origin is NOT a tenant — 'login' is a reserved
  // label. Throwing keeps this function's contract honest ("returns a
  // tenant slug or throws"), and both callers already catch: `apiClient`
  // falls back to 'aeeiee' (harmless — the central page only calls
  // tenant-agnostic routes, so the header value is ignored by the BE)
  // and `resolveTenantForWs` returns '' (the WS never connects on the
  // central origin anyway — the authenticated shell never mounts there).
  if (isCentralAuthHost(hostname)) {
    throw new Error(`Central auth origin is not a tenant host: ${host}`)
  }

  const label = hostname.slice(0, -APEX.length)

  // Strip trailing -staging or -prod
  return label.replace(/-staging$|-prod$/, '')
}

/**
 * Origin of the public marketing site that hosts the Terms of Service
 * and Privacy Policy. Staging hosts (`*-staging.3mote.com`,
 * `login-staging.3mote.com`) and localhost point at the staging site so
 * dev and staging never link into prod; everything else gets the prod
 * `www` origin. Prod hosts are suffix-less, so "not staging, not
 * localhost" is the prod branch. Pure — takes the host (port-tolerant).
 */
export function marketingSiteOrigin(host: string): string {
  const hostname = hostnameOf(host)
  if (isLocalhostHost(host) || hostname.endsWith(`-staging${APEX}`)) {
    return 'https://staging.3mote.com'
  }
  return 'https://www.3mote.com'
}
